import React, { FC, memo, useCallback, useLayoutEffect, useMemo, useState, useEffect } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import i18n from 'app/config/i18Config/i18n';
import { Form } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { contractOptionsActions, getContractOptions, UnitType, useContractOptions } from 'entities/Contract';
import { ModernRadioButtonGroup, RadioOptionTheme } from 'shared/ui/ModernRadioButtonGroup';
import { useCheckUserDataComplete, UserAccountType, User, getLoggedUserData, useAuthorizedState } from 'entities/User';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { Box } from 'entities/Box';
import { WarehouseForBooking } from 'entities/Warehouse';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { ModernDatePicker } from 'shared/ui/ModernDatePicker';
import { ModernSelect } from 'shared/ui/ModernSelect';
import { InfoTag } from 'shared/ui/InfoTag';
import { useGetCurrencySymbol } from 'app/appState';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { PaymentFrequencySetting } from 'entities/RentOption';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { AppLanguage } from 'app/config/i18Config/types';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { Toggle } from 'shared/ui/Toggle';
import { ModernInput } from 'shared/ui/ModernInput';
import { Button } from 'shared/ui/Button';
import { Checkbox } from 'shared/ui/Checkbox';
import { ReserveBox } from 'features/ReserveBox';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface ModernContractDetailsProps {
  selectedWarehouse: Nullable<WarehouseForBooking>;
  boxInfo: Nullable<Box>;
  userInfo: Nullable<User>;
  userAccountType: UserAccountType | undefined;
  selectedWarehouseId: string | undefined;
  isValidForm: boolean;
  onReservationSuccess: () => void;
  goToNextStep: () => void;
}

export const ModernContractDetails: FC<ModernContractDetailsProps> = memo((props) => {
  const { selectedWarehouse, boxInfo, userInfo, userAccountType, isValidForm, onReservationSuccess, goToNextStep } = props;

  const { t } = useAppTranslation('booking');
  const dispatch = useAppDispatch();

  const currentLanguage = i18n.language as AppLanguage;
  const currencySymbol = useGetCurrencySymbol();

  const { parsedQuery } = useQueryString();

  const { scrollToElement } = useScrollToElement('center', 500);

  useLayoutEffect(() => {
    scrollToElement();
  }, [scrollToElement]);

  const isUserDataCompleted = useCheckUserDataComplete();
  const isAuthorized = useAuthorizedState();
  const loggedUser = useAppSelector(getLoggedUserData);
  const contractOptions = useAppSelector(getContractOptions);
  const needAuthorization = !isAuthorized || !isUserDataCompleted;

  // Account type logic
  const accountTypeOptions = [
    { value: UserAccountType.PERSONAL, label: t('Personal'), theme: RadioOptionTheme.CLEAR },
    { value: UserAccountType.BUSINESS, label: t('Business'), theme: RadioOptionTheme.CLEAR },
  ];

  const changeAccountType = useCallback(
    (accountType: UserAccountType): void => {
      dispatch(contractOptionsActions.setContractOptions({ accountType }));
    },
    [dispatch],
  );

  const {
    contractOptions: contractOptionsFromHook,
    insuranceOptions,
    availableRentOptions,
    changeRentOption,
    handleEndDateChange,
    disabledEndDate,
    handleInsuranceChange,
    disabledStartDate,
    handleEntirePaymentChange,
    handleStartDateChange,
    firstAvailableEndDate,
    firstAvailableStartDate,
    useUserBalance,
    setUseUserBalance,
    setPromoCodeValue,
    promoDiscounts,
    isLoading,
  } = useContractOptions({
    userAccountType,
    user: userInfo,
    selectedWarehouse,
    box: boxInfo,
  });

  const [promoCode, setPromoCode] = useState<Nullable<string>>(contractOptionsFromHook.promoCodeValue);
  const [hasPromoCode, setHasPromoCode] = useState<boolean>(false);

  const isPromoApplied = useMemo(() => Boolean(promoDiscounts?.length), [promoDiscounts]);

  const isWrongPromo = useMemo(() => promoDiscounts?.length === 0, [promoDiscounts]);

  const contractDurationOptions = useMemo(() => {
    return availableRentOptions.map((rentOption) => ({
      label: getLocalizedString(rentOption.label),
      value: rentOption.rentOptionId,
      theme: rentOption.isSpecialPromotion ? RadioOptionTheme.WARNING : RadioOptionTheme.DEFAULT,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRentOptions, currentLanguage]);

  const isReserveDisabled = useMemo<boolean>(() => {
    const insuranceNeeded = boxInfo?.unitType === UnitType.BOX && Boolean(insuranceOptions?.length);
    let isDisabled =
      !isUserDataCompleted ||
      !selectedWarehouse ||
      !boxInfo ||
      !contractOptions.startDate ||
      (insuranceNeeded && !contractOptions.insuranceId);

    if (contractOptions.invoiceFrequencyType === 'day') {
      isDisabled = isDisabled || !contractOptions.endDate;
    }

    return isDisabled;
  }, [
    boxInfo,
    contractOptions.endDate,
    contractOptions.insuranceId,
    contractOptions.invoiceFrequencyType,
    contractOptions.startDate,
    isUserDataCompleted,
    selectedWarehouse,
    insuranceOptions,
  ]);

  const changeContractDuration = useCallback(
    (rentOptionId: string) => {
      const selectedRentOption = availableRentOptions.find((rentOption) => rentOption.rentOptionId === rentOptionId);

      if (selectedRentOption) {
        changeRentOption(selectedRentOption);
        setPromoCode(contractOptionsFromHook.promoCodeValue || '');
      }
    },
    [availableRentOptions, changeRentOption, contractOptionsFromHook.promoCodeValue],
  );

  useEffect(() => {
    parsedQuery.rentOptionId && availableRentOptions && changeContractDuration(parsedQuery.rentOptionId);
  }, [availableRentOptions, parsedQuery.rentOptionId, changeContractDuration]);

  // Auto-check promo code checkbox when promo is applied
  useEffect(() => {
    if (isPromoApplied && !hasPromoCode) {
      setHasPromoCode(true);
    }
  }, [isPromoApplied, hasPromoCode]);

  return (
    <div className="bg-white rounded-lg p-6 space-y-4">
      {/* Header Row: Booking title and Account Type selector */}
      <div className="flex items-center justify-between pb-4">
        <div className="font-semibold text-24 text-primary font-tektur">{t('Booking')}</div>
        <div className="ml-4 min-w-[235px]">
          <ModernRadioButtonGroup
            options={accountTypeOptions}
            value={userAccountType}
            onChange={changeAccountType}
            isDisabled={isUserDataCompleted}
          />
        </div>
      </div>

      <div>
        {/* Main Layout: Rent Options (Left) and Date/Coverage (Right) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
          {/* Left Side: Contract Duration (Rent Options) */}
          <div>
            <div className="font-semibold text-lg mb-1 font-tektur">{t('Tariffs')}</div>
            {/* <div className="text-sm text-primaryLight mb-3">
              <sup>*</sup>
              {t(
                'It is a permanent contract with a minimum contract period. After the minimum contract period has expired, the contract is automatically renewed monthly until it is terminated with a notice period of {{notifyLeaveDays}} days.',
                {
                  notifyLeaveDays: globalSettings?.notifyLeaveDays?.month || 14,
                },
              )}
            </div> */}
            <div>
              {isLoading ? (
                <div className="space-y-3">
                  <div className="h-6 bg-secondaryLight rounded w-1/2" />
                  <div className="h-6 bg-secondaryLight rounded w-2/3" />
                  <div className="h-6 bg-secondaryLight rounded w-1/3" />
                </div>
              ) : (
                <ModernRadioButtonGroup
                  options={contractDurationOptions}
                  direction="vertical"
                  withBorder
                  onChange={changeContractDuration}
                  value={contractOptionsFromHook?.rentOption?.rentOptionId}
                  labelPosition="post"
                  optionType="default"
                />
              )}
            </div>
            {!isLoading && contractOptionsFromHook?.rentOption?.description ? (
              <div className="text-md text-grey mt-2">{getLocalizedString(contractOptionsFromHook.rentOption.description)}</div>
            ) : null}
          </div>

          {/* Right Side: Move-in Date and Coverage */}
          <div className="flex flex-col items-end">
            {/* Move-in Date */}
            <div className="mb-[38px]">
              <div className="font-semibold text-lg mb-1 font-tektur">{t('Move-in date')}</div>
              {isLoading ? (
                <div className="h-14 bg-secondaryLight rounded w-[280px]" />
              ) : (
                <ModernDatePicker
                  className="w-[280px]"
                  format={CLIENT_DATE_FORMAT}
                  onChange={handleStartDateChange}
                  placeholder={t(CLIENT_DATE_FORMAT)}
                  inputReadOnly
                  disabledDate={disabledStartDate}
                  value={contractOptionsFromHook.startDate}
                  defaultPickerValue={firstAvailableStartDate}
                />
              )}
            </div>

            {/* Move-out Date (conditional) */}
            {contractOptionsFromHook.invoiceFrequencyType === InvoiceFrequencyType.DAY && (
              <div>
                <div className="font-semibold text-lg mb-1 font-tektur">{t('Move-out date')}</div>
                {isLoading ? (
                  <div className="h-14 bg-secondaryLight rounded w-[280px]" />
                ) : (
                  <ModernDatePicker
                    format={CLIENT_DATE_FORMAT}
                    className="w-[280px]"
                    onChange={handleEndDateChange}
                    placeholder={t(CLIENT_DATE_FORMAT)}
                    disabled={!contractOptionsFromHook.startDate}
                    inputReadOnly
                    requiredMark
                    disabledDate={disabledEndDate}
                    showToday={false}
                    value={contractOptionsFromHook.endDate}
                    defaultPickerValue={firstAvailableEndDate}
                  />
                )}
              </div>
            )}

            {/* Insurance */}
            <div className="w-[280px]">
              {insuranceOptions?.length ? <div className="font-semibold text-lg mb-1 font-tektur">{t('Coverage amount')}</div> : null}
              <Form.Item name="insuranceId">
                {!insuranceOptions?.length ? null : (
                  <div className="w-[280px]">
                    <ModernSelect
                      className="w-[280px]"
                      placeholder={t('Select')}
                      onChange={handleInsuranceChange}
                      options={insuranceOptions}
                      value={contractOptionsFromHook.insuranceId}
                    />
                    {contractOptionsFromHook.insuranceId && (
                      <div className="text-md mt-2 pl-1 text-grey">
                        {t(`{{rate}} {{currencySymbol}}/${contractOptionsFromHook.invoiceFrequencyType}`, {
                          rate: formatPrice(contractOptionsFromHook.insuranceRate),
                          currencySymbol,
                        })}
                      </div>
                    )}
                  </div>
                )}
              </Form.Item>
            </div>
          </div>
        </div>

        {/* Promo Code */}
        <div className="mb-6">
          <div className="flex items-center mb-3">
            <Checkbox
              name="hasPromoCode"
              checked={hasPromoCode}
              disabled={isPromoApplied}
              onChange={(checked) => {
                if (!isPromoApplied) {
                  setHasPromoCode(checked);
                  if (!checked) {
                    setPromoCode('');
                    setPromoCodeValue('');
                  }
                }
              }}
            />
            <span className="ml-2 font-semibold text-base">{t('Have a promo code ?')}</span>
          </div>

          {hasPromoCode && (
            <div>
              {isPromoApplied && <div className="font-semibold text-success mb-1">{t('Promo has been successfully applied')}</div>}
              {isWrongPromo && <div className="font-semibold text-error mb-1">{t('Wrong promo code, please try another one')}</div>}
              <div className="flex items-center">
                <ModernInput
                  onChange={setPromoCode}
                  value={promoCode}
                  containerClassName="w-full max-w-[200px]"
                  className="rounded-tr-none rounded-br-none"
                  placeholder={t('Promo code')}
                  disabled={isPromoApplied}
                  bordered={false}
                  uppercase
                />
                <div className="transform -translate-x-[5px]">
                  <Button
                    isDisabled={isPromoApplied}
                    theme="dark"
                    onClick={() => {
                      setPromoCodeValue(promoCode);
                    }}
                  >
                    {t('Apply')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Balance and Prepayment */}
        <div className="flex gap-6 flex-col-reverse desktop:flex-row desktop:space-x-6">
          {Boolean(userInfo?.balance) && (
            <div className="flex-1">
              <InfoTag textSize="small">
                {t('You have {{balance}} {{currencySymbol}} on your balance, you can use it to pay your booking', {
                  balance: formatPrice(userInfo?.balance),
                  currencySymbol,
                })}
              </InfoTag>
              <div className="font-semibold text-base mt-4 mb-1">{t('Personal balance')}</div>
              <div className="flex items-center w-full max-w-[580px] rounded-lg p-2 border border-secondaryAccent">
                <Toggle checked={useUserBalance} onChange={setUseUserBalance} />
                <div className="text-m">{t('Use your balance')}</div>
              </div>
            </div>
          )}
          {contractOptionsFromHook?.rentOption?.paymentFrequencySetting === PaymentFrequencySetting.ALL && (
            <div className="flex-1">
              {/* <InfoTag textSize="small">{t('Discount information for entire period!')}</InfoTag> */}
              <div className="font-semibold text-base mt-4 mb-1">{t('Prepayment')}</div>
              <div className="flex items-center w-full max-w-[580px] rounded-lg p-2 border border-secondaryAccent">
                <Toggle checked={contractOptionsFromHook.payForEntirePeriod} onChange={handleEntirePaymentChange} />
                <div className="text-m">{t('Pay for the entire period')}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions Section */}
      <div className="pt-2">
        <div className="flex space-x-4">
          {!needAuthorization && (
            <ReserveBox
              action={
                <Button containerClassName="w-full" size="medium" theme="primary-outlined" isDisabled={isLoading || isReserveDisabled}>
                  {t('Reserve')}
                </Button>
              }
              boxId={boxInfo?.boxId}
              warehouseId={selectedWarehouse?.warehouseId}
              contractOptions={contractOptions}
              userId={loggedUser?.userId}
              onSuccess={onReservationSuccess}
              needConfirmBookingDocs
            />
          )}
          <Button containerClassName="w-full" size="medium" type="submit" isDisabled={isLoading || !isValidForm} onClick={goToNextStep}>
            {t('Continue')}
          </Button>
        </div>
      </div>
    </div>
  );
});
